/**
 * "Do Not Sell or Share My Personal Information": the visitor's choice about
 * advertising and analytics tags, and how it reaches the server.
 *
 * Defera loads advertising tags (the ChatGPT Ads pixel in index.html, the
 * Google Ads tag) and an analytics tag (Contentsquare) on public pages, and
 * an hourly job reports bookings to OpenAI's ad measurement with a hashed
 * email (server/openai-ads-conversions.js). The Privacy Policy promises a
 * right to opt out of that. This module is the switch:
 *
 * - A visitor opts out on /privacy-choices (stored on this browser), or their
 *   browser sends Global Privacy Control, which counts as an opt-out.
 * - While opted out, `marketingAllowed()` is false: <MarketingTags> renders
 *   nothing, and index.html's inline pixel checks the same two signals before
 *   it loads.
 * - A signed-in customer's opt-out is also recorded on their account
 *   (POST /api/privacy/opt-out), so the server stops reporting their bookings.
 *
 * index.html reads OPT_OUT_STORAGE_KEY by name: keep the two in step
 * (privacyChoices.test.js checks it).
 */

import { Capacitor } from '@capacitor/core';

export const OPT_OUT_STORAGE_KEY = 'defera.privacy.doNotSellOrShare';
/** Which account (and which choice) this browser last recorded on the server. */
export const SYNCED_STORAGE_KEY = 'defera.privacy.syncedFor';
/** Fired on window when the choice changes, so open views can re-render. */
export const PRIVACY_CHOICE_EVENT = 'defera:privacy-choice';

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** True when the browser sends Global Privacy Control (an opt-out signal we honor). */
export function gpcEnabled(nav = typeof navigator !== 'undefined' ? navigator : undefined) {
  return nav?.globalPrivacyControl === true;
}

/** True when this browser has opted out on /privacy-choices. */
export function hasOptedOutHere() {
  try {
    return storage()?.getItem(OPT_OUT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when advertising and analytics tags may load: no GPC signal and no opt-out. */
export function marketingAllowed(nav) {
  // The iOS app does no ad tracking (no Meta Pixel, Google Ads or Contentsquare),
  // which is why it needs no App Tracking Transparency prompt.
  if (Capacitor.isNativePlatform()) return false;
  return !gpcEnabled(nav) && !hasOptedOutHere();
}

/** Record this browser's choice. Returns the new effective opt-out state. */
export function setOptedOutHere(optedOut) {
  try {
    const s = storage();
    if (optedOut) s?.setItem(OPT_OUT_STORAGE_KEY, '1');
    else s?.removeItem(OPT_OUT_STORAGE_KEY);
  } catch {
    /* blocked storage: GPC still works, and a signed-in choice still reaches the server */
  }
  try {
    window.dispatchEvent(new CustomEvent(PRIVACY_CHOICE_EVENT));
  } catch {
    /* non-browser */
  }
  return !marketingAllowed();
}

/**
 * Tell the server about a signed-in customer's choice, once per account and
 * choice on this browser. `send` posts it (injected for tests). Never throws.
 */
export async function syncOptOutToAccount({ userId, send, nav }) {
  if (!userId || typeof send !== 'function') return false;
  const optedOut = !marketingAllowed(nav);
  const marker = `${userId}:${optedOut ? 'out' : 'in'}`;
  try {
    if (storage()?.getItem(SYNCED_STORAGE_KEY) === marker) return false;
  } catch {
    /* fall through and send */
  }
  // Nothing to record for a customer who never opted out on any browser.
  if (!optedOut && !hasSyncedBefore(userId)) return false;
  try {
    const ok = await send({ optedOut, source: gpcEnabled(nav) ? 'gpc' : 'page' });
    if (ok) storage()?.setItem(SYNCED_STORAGE_KEY, marker);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/** Note that the server already has this account's choice (after an explicit save). */
export function markSynced(userId, optedOut) {
  try {
    if (userId) storage()?.setItem(SYNCED_STORAGE_KEY, `${userId}:${optedOut ? 'out' : 'in'}`);
  } catch {
    /* non-fatal */
  }
}

function hasSyncedBefore(userId) {
  try {
    return String(storage()?.getItem(SYNCED_STORAGE_KEY) || '').startsWith(`${userId}:`);
  } catch {
    return false;
  }
}
